import { useEffect } from "react";

function pageTable():HTMLTableElement|null{
  const heading=[...document.querySelectorAll("h1")].find(x=>x.textContent?.trim()==="Welder Qualifications");
  return heading?.closest("section.content")?.querySelector(".table-card table") as HTMLTableElement|null;
}

function rowKey(row:HTMLTableRowElement){
  const cells=row.cells;
  const name=cells[0]?.querySelector("b")?.textContent?.trim()||cells[0]?.textContent?.trim()||"";
  const id=cells[1]?.querySelector("b")?.textContent?.trim()||cells[1]?.textContent?.trim()||"";
  return `${name}|${id}`;
}

const clean=(value:string)=>value.replace(/\s+/g," ").trim();
const unique=(values:string[])=>Array.from(new Set(values.map(clean).filter(Boolean)));

function restore(table:HTMLTableElement){
  table.querySelectorAll<HTMLTableRowElement>("tbody tr").forEach(row=>{
    if(row.dataset.groupedSummaryHidden==="true"){
      row.style.display="";
      delete row.dataset.groupedSummaryHidden;
    }
    [...row.cells].forEach(cell=>{
      if(cell.dataset.originalGroupedHtml!==undefined){
        cell.innerHTML=cell.dataset.originalGroupedHtml;
        delete cell.dataset.originalGroupedHtml;
      }
      cell.style.display="";
      cell.classList.remove("welder-summary-processes","welder-summary-programs","welder-summary-count","welder-summary-expiry","welder-summary-status");
    });
    row.classList.remove("welder-summary-row");
  });
}

function setHeaders(table:HTMLTableElement){
  const headers=[...table.querySelectorAll<HTMLTableCellElement>("thead th")];
  const labels=["WELDER","WELDER ID","PROGRAMS","QUALIFIED PROCESSES","ACTIVE QUALIFICATIONS","NEXT EXPIRY","STATUS"];
  labels.forEach((label,index)=>{if(headers[index])headers[index].textContent=label});
  for(let i=7;i<headers.length;i++)headers[i].style.display="none";
}

function earliestExpiry(rows:HTMLTableRowElement[]){
  const dates=rows.map(r=>clean(r.cells[7]?.textContent||"")).filter(x=>/^\d{4}-\d{2}-\d{2}$/.test(x)).sort();
  return dates[0]||"—";
}

function overallStatus(rows:HTMLTableRowElement[]){
  const statuses=rows.map(r=>(r.cells[8]?.textContent||"").toUpperCase());
  if(statuses.some(x=>x.includes("EXPIRED")))return {text:"● ATTENTION",className:"expired"};
  if(statuses.some(x=>x.includes("EXPIRING")))return {text:"● EXPIRING",className:"expiring"};
  return {text:"● ACTIVE",className:"active"};
}

function summarize(first:HTMLTableRowElement,groupRows:HTMLTableRowElement[]){
  [...first.cells].forEach(cell=>{cell.dataset.originalGroupedHtml=cell.innerHTML});
  first.classList.add("welder-summary-row");

  const programs=unique(groupRows.map(r=>clean(r.cells[2]?.textContent||"")));
  const processes=unique(groupRows.map(r=>clean(r.cells[3]?.textContent||"")));
  const status=overallStatus(groupRows);

  first.cells[2].classList.add("welder-summary-programs");
  first.cells[2].innerHTML=`<div class="welder-summary-chips">${programs.map(x=>`<span>${x}</span>`).join("")}</div>`;

  first.cells[3].classList.add("welder-summary-processes");
  first.cells[3].innerHTML=`<div class="welder-summary-chips process-chips">${processes.map(x=>`<span>${x}</span>`).join("")}</div>`;

  first.cells[4].classList.add("welder-summary-count");
  first.cells[4].innerHTML=`<b>${groupRows.length}</b><small>${groupRows.length===1?"qualification":"qualifications"}</small>`;

  first.cells[5].classList.add("welder-summary-expiry");
  first.cells[5].textContent=earliestExpiry(groupRows);

  first.cells[6].classList.add("welder-summary-status");
  first.cells[6].innerHTML=`<span class="status ${status.className}">${status.text}</span>`;

  for(let i=7;i<first.cells.length;i++)first.cells[i].style.display="none";
  groupRows.slice(1).forEach(row=>{row.style.display="none";row.dataset.groupedSummaryHidden="true"});
}

function group(){
  const table=pageTable();if(!table)return;
  restore(table);
  setHeaders(table);
  table.classList.add("grouped-qualification-table","welder-summary-table");

  const rows=[...table.querySelectorAll<HTMLTableRowElement>("tbody tr")].filter(row=>row.style.display!=="none");
  const groups=new Map<string,HTMLTableRowElement[]>();
  rows.forEach(row=>{
    const key=rowKey(row);if(!key||key==="|")return;
    const existing=groups.get(key)||[];existing.push(row);groups.set(key,existing);
  });
  groups.forEach(groupRows=>summarize(groupRows[0],groupRows));
}

export default function GroupedWelderQualificationList(){
  useEffect(()=>{
    let queued=false;
    const schedule=()=>{if(queued)return;queued=true;requestAnimationFrame(()=>{queued=false;group()})};
    const onClick=(event:Event)=>{
      const target=event.target as HTMLElement;
      if(target.closest(".division-control button")||target.closest(".metrics article"))setTimeout(schedule,0);
    };
    const onInput=(event:Event)=>{
      const target=event.target as HTMLInputElement;
      if(target.closest(".table-card .toolbar"))setTimeout(schedule,0);
    };
    document.addEventListener("click",onClick,true);
    document.addEventListener("input",onInput,true);
    const observer=new MutationObserver(mutations=>{
      if(mutations.some(m=>[...m.addedNodes,...m.removedNodes].some(n=>n instanceof HTMLTableRowElement||n instanceof HTMLTableSectionElement)))schedule();
    });
    observer.observe(document.body,{childList:true,subtree:true});
    schedule();
    return()=>{document.removeEventListener("click",onClick,true);document.removeEventListener("input",onInput,true);observer.disconnect()};
  },[]);
  return null;
}
